import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../api/client";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { User, Mail, Shield, ArrowLeft } from "lucide-react";
import Loader from "../../components/ui/Loader";

export default function AdminProfile() {
 const [user, setUser] = useState(null);
 const [loading, setLoading] = useState(true);
 const navigate = useNavigate();

 useEffect(() => {
 const fetchProfile = async () => {
 try {
 const res = await api.get("/auth/me");
 if (res.data && res.data.user) {
 setUser(res.data.user);
 }
 } catch (err) {
 console.error("Failed to fetch admin profile:", err);
 } finally {
 setLoading(false);
 }
 };
 fetchProfile();
 }, []);

 if (loading) return <Loader />;

 if (!user) {
 return (
 <div className="min-h-screen flex items-center justify-center">
 <div className="text-center">
 <p className="text-xl font-semibold mb-4">Admin profile not found.</p>
 <Button onClick={() => navigate("/admin/dashboard")}>Back to Dashboard</Button>
 </div>
 </div>
 );
 }

 return (
 <div className="min-h-screen py-6 md:py-10">
 <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
 {/* Header */}
 <div className="mb-8">
 <Button
 variant="outline"
 onClick={() => navigate("/admin/dashboard")}
 className="mb-6"
 >
 <ArrowLeft className="w-4 h-4 mr-2" />
 Back to Dashboard
 </Button>
 
 <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
 <div>
 <h1 className="text-3xl md:text-4xl font-bold mb-2">My Profile</h1>
 <p className="text-muted-foreground">Manage your administrator account</p>
 </div>
 <Button onClick={() => navigate("/admin/profile/edit")}>
 Edit Profile
 </Button>
 </div>
 </div>

 {/* Profile Card */}
 <Card>
 <CardHeader className="border-b pb-6">
 <div className="flex items-center gap-4">
 <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center border-2 border-primary/20">
 <User className="w-8 h-8 text-primary" />
 </div>
 <div>
 <CardTitle className="text-2xl font-bold">
 {user.fullName || "Admin User"}
 </CardTitle>
 <div className="flex items-center gap-2 mt-1">
 <Shield className="w-4 h-4 text-primary" />
 <span className="text-sm font-medium text-primary uppercase tracking-wider">
 {user.role}
 </span>
 </div>
 </div>
 </div>
 </CardHeader>
 <CardContent className="pt-6 space-y-6">
 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
 <Detail 
 icon={<Mail className="w-5 h-5 text-muted-foreground" />} 
 label="Email Address" 
 value={user.email} 
 />
 <Detail 
 icon={<Shield className="w-5 h-5 text-muted-foreground" />} 
 label="Verification Status" 
 value={
 <span className={`flex items-center gap-1.5 ${user.isVerified ? "text-green-600" : "text-amber-600"}`}>
 <span className={`w-2 h-2 rounded-full ${user.isVerified ? "bg-green-600" : "bg-amber-600"}`} />
 {user.isVerified ? "Verified" : "Pending"}
 </span>
 } 
 />
 </div>
 </CardContent>
 </Card>
 </div>
 </div>
 );
}

function Detail({ icon, label, value }) {
 return (
 <div className="space-y-1.5">
 <div className="flex items-center gap-2 text-muted-foreground">
 {icon}
 <h3 className="text-sm font-semibold uppercase tracking-wider">{label}</h3>
 </div>
 <div className="text-base font-medium px-1">
 {value || <span className="text-muted-foreground/50 italic">Not set</span>}
 </div>
 </div>
 );
}
